import { Component, Host, h, Prop, Event, EventEmitter, Method, Element } from '@stencil/core';
import { Filter } from '../../03_organisms/lilu-product-list/filter';
import { FilterChangedEvent } from '../../03_organisms/lilu-product-list/filter-changed-event';
import Debounce from 'debounce-decorator';
import { ActivatedFilter } from '../../02_molecules/lilu-activated-filters/actived-filter';

@Component({
  tag: 'lilu-range-slider',
  styleUrl: 'lilu-range-slider.scss',
  shadow: true,
})
export class LiluRangeSlider implements Filter {
  @Prop({ attribute: 'valueLeft' }) valueLeft: string;
  @Prop({ attribute: 'valueRight' }) valueRight: string;
  @Prop() min: string;
  @Prop() max: string;
  @Prop() step = '1';
  @Prop() inputid: string;
  @Prop() idleft: string;
  @Prop() idright: string;
  @Prop() label: string;
  @Prop() unit: string;
  @Prop() unitpostfix?: string;
  @Prop() toLabel: string;
  @Prop() dataControlFilter: string;

  @Element() el: HTMLElement;

  private rangeInputLeft!: HTMLInputElement;
  private rangeInputRight!: HTMLInputElement;
  private numberInputLeft!: HTMLInputElement;
  private numberInputRight!: HTMLInputElement;

  private oldValue: string;

  @Event()
  filterChanged: EventEmitter<FilterChangedEvent>;

  componentDidLoad() {
    this.rangeInputLeft.value = this.valueLeft;
    this.rangeInputRight.value = this.valueRight;
    this.numberInputLeft.value = this.valueLeft;
    this.numberInputRight.value = this.valueRight;
    this.renderSlider();
  }

  @Method()
  async getActiveFilterByValue(_: string): Promise<ActivatedFilter> {
    return new Promise((resolve, _) => {
      const valueFrom = Math.min(+this.numberInputLeft.value, +this.numberInputRight.value);
      const valueTo = Math.max(+this.numberInputLeft.value, +this.numberInputRight.value);
      const activatedFilter: ActivatedFilter = {
        filterControlName: this.dataControlFilter,
        label: `${this.unit} ${valueFrom}${this.unitpostfix} ${this.toLabel} ${this.unit} ${valueTo}${this.unitpostfix}`,
        value: `[${valueFrom}+TO+${valueTo}]`,
        multiSelect: false
      };
      resolve(activatedFilter);
    });
  }

  @Method()
  async deactivateFilter(_: string) {
    this.resetRangeSlider();
  }

  @Method()
  async resetAllFilters() {
    this.resetRangeSlider();
  }

  /**
   * Set the min / max value to the input fields and trigger the change event
   */
  resetRangeSlider() {
    this.rangeInputLeft.value = this.min;
    this.numberInputLeft.value = this.min;
    this.rangeInputRight.value = this.max;
    this.numberInputRight.value = this.max;
    this.renderSlider();
  }

  @Debounce(1000)
  onRangeChange() {
    const valueFrom = Math.min(+this.rangeInputLeft.value, +this.rangeInputRight.value);
    const valueTo = Math.max(+this.rangeInputLeft.value, +this.rangeInputRight.value);

    let remove = false;
    // Remove the filter if it has inital values
    if (valueFrom === +this.min && valueTo === +this.max) {
      remove = true;
    }

    const filterChangeEvent: FilterChangedEvent = {
      filterControlName: this.dataControlFilter,
      label: `${this.unit} ${valueFrom}${this.unitpostfix} ${this.toLabel} ${this.unit} ${valueTo}${this.unitpostfix}`,
      value: `[${valueFrom}+TO+${valueTo}]`,
      remove,
      multiSelect: false
    };
    this.filterChanged.emit(filterChangeEvent);
  }

  updateInputValue(event, side: string) {
    if (side === 'left') {
      this.rangeInputLeft.value = parseInt(event.target.value).toString();
      this.numberInputLeft.value = parseInt(event.target.value).toString();
      this.numberInputLeft.blur()
    } else {
      this.rangeInputRight.value = parseInt(event.target.value).toString();
      this.numberInputRight.value = parseInt(event.target.value).toString();
      this.numberInputRight.blur()
    }
    this.renderSlider();
  }

  renderSlider() {
    const lowerValue = Math.min(+this.numberInputLeft.value, +this.rangeInputRight.value)
    const heigherValue = Math.max(+this.numberInputLeft.value, +this.rangeInputRight.value)

    const rangeDistance = +this.max - +this.min;
    const fromPosition = lowerValue - +this.min;
    const toPosition = heigherValue - +this.min;

    const toSlider = this.el.shadowRoot.querySelector('#toSlider') as HTMLInputElement;
    toSlider.style.background = `linear-gradient(
      to right,
      #F1EFEF 0%,
      #F1EFEF ${(fromPosition) / (rangeDistance) * 100}%,
      #413F41 ${((fromPosition) / (rangeDistance)) * 100}%,
      #413F41 ${(toPosition) / (rangeDistance) * 100}%,
      #F1EFEF ${(toPosition) / (rangeDistance) * 100}%,
      #F1EFEF 100%)`;

    // Show input field with lower value on the left side
    const inputOrder = +this.numberInputLeft.value > +this.numberInputRight.value ? 'row-reverse' : 'initial';
    const rangeSliderLables = this.el.shadowRoot.querySelector('.range-slider-labels') as HTMLInputElement;
    rangeSliderLables.style.flexDirection = inputOrder;
  }

  onFocus(side: string) {
    if (side === 'left') {
      this.oldValue = this.numberInputLeft.value;
      this.numberInputLeft.select();
    } else {
      this.oldValue = this.numberInputRight.value;
      this.numberInputRight.select();
    }
  }

  onChange(evt, side: string) {
    const isNewValueValid: boolean = parseInt(evt.target.value) >= +this.min && parseInt(evt.target.value) <= +this.max;

    if (!isNewValueValid) {
      if (side === 'left') {
        this.numberInputLeft.parentElement.classList.add('invalid')

        setTimeout(() => {
          this.numberInputLeft.parentElement.classList.remove('invalid')
          this.numberInputLeft.value = this.oldValue;
        }, 1400);
      } else {
        this.numberInputRight.parentElement.classList.add('invalid')

        setTimeout(() => {
          this.numberInputRight.parentElement.classList.remove('invalid');
          this.numberInputRight.value = this.oldValue;
        }, 1400);
      }
    } else {
      this.updateInputValue(evt, side);
      this.renderSlider();
      this.onRangeChange();
    }
  }

  onKeyUp(evt) {
    // key code for enter
    if (evt.keyCode === 13) {
      evt.target.blur();
    }
  }

  render() {
    return (
      <Host>
        <div class="range_container">
          <div class="sliders_control">
            <input
              id="fromSlider"
              type="range"
              ref={el => this.rangeInputLeft = el as HTMLInputElement}
              min={this.min}
              max={this.max}
              step={this.step}
              onInput={event => this.updateInputValue(event, 'left')}
              onChange={_ => this.onRangeChange()} />
            <input
              id="toSlider"
              type="range"
              ref={el => this.rangeInputRight = el as HTMLInputElement}
              min={this.min}
              max={this.max}
              step={this.step}
              onInput={event => this.updateInputValue(event, 'right')}
              onChange={_ => this.onRangeChange()} />
          </div>
          <div class="range-slider-labels">
            <div class="range-slider-label-container" style={+this.numberInputLeft?.value > +this.numberInputRight?.value ? { order: '1' } : null}>
              <label class="range-slider-label" htmlFor={this.idleft}>
                {this.unit}
              </label>
              <input
                class="range-slider-value-input input-left"
                type="number"
                id={this.idleft}
                ref={el => this.numberInputLeft = el as HTMLInputElement}
                min={this.min}
                max={this.max}
                onFocus={_ => this.onFocus('left')}
                onChange={evt => this.onChange(evt, 'left')}
                onKeyUp={evt => this.onKeyUp(evt)} />
              {this.unitpostfix ? <span class="range-slider-label">{this.unitpostfix}</span> : null}
            </div>
            <div class="range-slider-label-container">
              <label class="range-slider-label" htmlFor={this.idright}>
                {this.unit}
              </label>
              <input
                class="range-slider-value-input input-right"
                type="number"
                id={this.idright}
                ref={el => this.numberInputRight = el as HTMLInputElement}
                min={this.min}
                max={this.max}
                onFocus={_ => this.onFocus('right')}
                onChange={evt => this.onChange(evt, 'right')}
                onKeyUp={evt => this.onKeyUp(evt)} />
              {this.unitpostfix ? <span class="range-slider-label">{this.unitpostfix}</span> : null}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
